/**
 * Renders a FixedSideBar that contains an image,
 * Suite sections, and a button
 */
/** @jsx jsx */
import PropTypes from 'prop-types';
import { css, jsx } from '@emotion/core';
import Image from '../../atoms/Image/Image';
import sanityImage from '../../../utils/sanityImage';
import Button from '../../atoms/Buttons/Button/Button';
import Card from '../../atoms/Card/Card';
import { Heading4 } from '../../atoms/Headings/Headings';
import {
  breakpoints,
  colors,
  pageDimensions,
  shadows,
  unitConverter as uc,
} from '../../../styles/base';

import ClientSideBar from './ClientSideBar';
import IndustrySideBar from './IndustrySideBar';
import SubscriptionSideBar from './SubscriptionSideBar';

/**
 * The <FixedSideBar /> Component
 */

const FixedSideBarV1 = ({
  _type,
  button,
  clientEntity,
  heading,
  productCategoryEntity,
  sidebarText,
  ...props
}) => {
  const logo = clientEntity.clientLogoColor;
  const align = 'left';

  const industryPage = _type === 'industryFixedSideBar';

  const fixedSideBarCss = css`
    position: absolute;
    top: ${uc('220px')};
    right: ${uc('45px')};
    z-index: 3;
    width: ${uc('437px')};
    padding-bottom: ${uc('50px')};
    text-align: center;
    background: ${colors.lightGray.six};

    div > .icon-link-subheading {
      padding-bottom: 0;
      text-align: left;

      p {
        white-space: normal;
      }
    }

    @media (max-width: ${pageDimensions.smallDesktopWidth}) {
      top: ${uc('-50px')};
    }

    @media (${breakpoints.largeTablet}) {
      position: relative;
      top: 0;
      right: auto;
      width: 95%;
      margin: ${uc('0 auto 40px')};
      margin-top: ${uc('20px')};
      padding: ${uc('1px 0')}; /* fix padding bug where it disappears */
    }
  `;

  const sidebarContentCss = css`
    margin: ${uc('40px auto')};
    margin-bottom: ${_type === 'subscriptionFixedSideBar' ? uc('0px') : 'auto'};
    padding: ${uc('0 20px')};
    border-radius: 25%;
  `;

  const logoCss = css`
    max-height: ${uc('196px')};
    margin-bottom: ${industryPage ? uc('10px') : uc('40px')};
    border-radius: 50%;
    box-shadow: ${shadows.large};
  `;

  const heading4Css = css`
    margin-bottom: ${industryPage ? 0 : uc('-10px')};
    margin-left: ${align === 'left' ? uc('30px') : '0'};
    text-align: ${align};

    @media (${breakpoints.largeTablet}) {
      margin: ${uc('0 0 20px 0')};
      text-align: center;
    }
  `;

  let render;

  switch (_type) {
    case 'clientFixedSideBarV1':
      render = (
        <ClientSideBar
          content={props}
          clientProducts={clientEntity.clientProducts}
          heading4Css={heading4Css}
          heading={sidebarText}
          productCategoryEntity={productCategoryEntity}
        />
      );
      break;
    case 'industryFixedSideBar':
      render = <IndustrySideBar content={props} />;
      break;
    case 'subscriptionFixedSideBar':
      render = <SubscriptionSideBar content={props} />;
      break;
    default:
      break;
  }
  return (
    <Card css={fixedSideBarCss} id="fixed-sidebar">
      <div css={sidebarContentCss}>
        <Image
          css={logoCss}
          src={sanityImage(logo)
            .height(196)
            .fit('max')
            .url()}
          alt="logo"
        />
        {(_type !== 'clientFixedSideBarV1' ||
          (typeof clientEntity.clientProducts !== 'undefined' &&
            clientEntity.clientProducts.length > 0)) && (
          <Heading4 css={heading4Css}>{heading}</Heading4>
        )}
        {render}
        {button.text && <Button {...button}>{button.text}</Button>}
      </div>
    </Card>
  );
};

/**
 * Prop types
 */
FixedSideBarV1.propTypes = {
  align: PropTypes.string,
  button: PropTypes.instanceOf(Object),
  clientEntity: PropTypes.shape({
    clientLogoColor: PropTypes.shape({ asset: {}, alt: PropTypes.string }),
    clientProductCategory: PropTypes.arrayOf(PropTypes.string),
    clientProducts: {},
  }),
  children: PropTypes.node,
  data: PropTypes.shape({}),
  heading: PropTypes.string,
  logo: PropTypes.shape({}),
  productCategoryEntity: PropTypes.arrayOf(PropTypes.shape({})),
  sidebarText: PropTypes.string,
  suiteSection: PropTypes.shape({}),
  _type: PropTypes.string,
};

/**
 * Default props
 */
FixedSideBarV1.defaultProps = {
  align: 'left',
  button: {},
  clientEntity: {},
  children: null,
  data: {},
  heading: 'Products used',
  logo: {},
  productCategoryEntity: [],
  sidebarText: '',
  suiteSection: {},
  _type: '',
};

export default FixedSideBarV1;
